import { Centrifuge } from "centrifuge";
import axios from "axios";

class CentrifugeClient {
  constructor() {
    this.centrifuge = null;
    this.subscriptions = {};
    this.connected = false;
    this.payload = null;
    this.isConnecting = false;
    this.connectPromise = null;
    this.pendingSubscriptions = [];
  }

  getEndpoint() {
    // Если REACT_APP_USE_CENTRIFUGO_PORT=true, используем текущий домен с портом
    const usePort = process.env.REACT_APP_USE_CENTRIFUGO_PORT === "true";

    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    const apiDomain = process.env.REACT_APP_DOMAIN;
    const port = process.env.REACT_APP_CENTRIFUGO_PORT;

    if (usePort) {
      return `${protocol}://${apiDomain}:${port}/connection/websocket`;
    } else {
      return `${protocol}://${apiDomain}/connection/websocket`;
    }
  }

  async getSubscriptionToken() {
    const apiUrl =
      process.env.REACT_APP_SCHEMA +
      (process.env.REACT_APP_PORT
        ? process.env.REACT_APP_DOMAIN + ":" + process.env.REACT_APP_PORT
        : process.env.REACT_APP_DOMAIN);

    // Используем публичный эндпоинт для мониторов (без авторизации)
    const response = await axios.post(
      apiUrl + "/api/v1.0/public/web-socket/subscribe-monitor",
      {},
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response.data.response ?? response.data;
  }

  async connect() {
    if (this.centrifuge) return;
    if (this.isConnecting && this.connectPromise) return this.connectPromise;

    this.isConnecting = true;
    this.connectPromise = (async () => {
      try {
        this.payload = await this.getSubscriptionToken();

        const endpoint = this.getEndpoint();
        const instance = new Centrifuge(endpoint, {
          token: this.payload.token,
        });

        instance.on("connected", () => {
          console.log("Centrifuge connected");
          this.connected = true;

          // Process pending subscriptions
          for (const { channel, handler } of this.pendingSubscriptions) {
            this.subscribe(channel, handler);
          }
          this.pendingSubscriptions.length = 0;
        });

        instance.on("disconnected", (ctx) => {
          console.warn(
            "Centrifuge disconnected:",
            ctx.reason,
            "Code:",
            ctx.code
          );
          this.connected = false;
        });

        instance.on("error", (error) => {
          console.error("Centrifuge error:", error);
        });

        instance.connect();
        this.centrifuge = instance;
      } catch (error) {
        console.error("Failed to connect to Centrifuge:", error);
        this.isConnecting = false;
        throw error;
      }
    })();

    await this.connectPromise;
    this.isConnecting = false;
  }

  subscribe(channel, handler) {
    if (!this.centrifuge) {
      if (this.isConnecting) {
        this.pendingSubscriptions.push({ channel, handler });
        return;
      }

      this.connect()
        .then(() => {
          this.subscribe(channel, handler);
        })
        .catch((error) => {
          console.error(
            `Failed to connect to Centrifuge for channel "${channel}":`,
            error
          );
        });

      this.pendingSubscriptions.push({ channel, handler });
      return;
    }

    if (this.subscriptions[channel]) {
      return;
    }

    const token = this.payload?.subs?.[channel];
    if (!token) {
      console.warn(`No subscription token found for channel "${channel}"`);
      return;
    }

    const sub = this.centrifuge.newSubscription(channel, { token });

    sub.on("publication", (ctx) => {
      if (handler) {
        handler(ctx.data);
      }
    });

    sub.on("error", (error) => {
      console.error(`Subscription error for channel "${channel}":`, error);
    });

    sub.subscribe();
    this.subscriptions[channel] = sub;
  }

  unsubscribe(channel) {
    const sub = this.subscriptions[channel];
    if (sub) {
      sub.unsubscribe();
      delete this.subscriptions[channel];
    }
  }

  disconnect() {
    if (this.centrifuge) {
      this.centrifuge.disconnect();
      this.centrifuge = null;
      Object.keys(this.subscriptions).forEach((key) =>
        delete this.subscriptions[key]
      );
      this.connected = false;
    }
  }
}

export default CentrifugeClient;
